/**
 * "Show me this before I attach it" — the preview behind a table or variable
 * name in the agent side panel, and behind a chip in the composer.
 *
 * One component for both kinds, keyed off the chip itself, because a chip is
 * the only handle either caller has: the panel builds one from the tree node it
 * was clicked on, the composer already holds one. Nothing else has to know
 * which kind it is looking at.
 *
 * Tables reuse <TableCitationModal>, the same popup a Gen AI citation and a
 * chart's "View table" link open, so a table looks identical wherever it is
 * previewed from. It lives under dashboard/widgets for historical reasons only
 * — it takes no editor props and was extracted for reuse (#1135).
 *
 * A variable has no table to draw, so its preview is its code list. That is a
 * modest thing to look at, and deliberately so: the question it asks and the
 * answers it allows are exactly what you need to know before attaching it.
 */
import React from 'react'
import {
  Box, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton,
  List, ListItem, ListItemText, Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import axios from 'axios'
import { useQuery } from '@tanstack/react-query'

import TableCitationModal from '../dashboard/widgets/TableCitationModal'
import InlineDashboard from './InlineDashboard'
import useDashboards from '../../hooks/useDashboards'
import { API_BASE_URL } from '../../constants/system'
import { GenNode } from '../../types/carbon/models/GenNode'
import { getNodeTitle } from '../../utils/nodeTypes'
import type { ComposerChip } from '../../hooks/useComposerChips'

export interface AgentAttachmentPreviewProps {
  /** What to preview. Null closes the dialog. */
  chip: ComposerChip | null
  customer: string
  onClose: () => void
}

/** The variable half: its codes, fetched on open. */
function VariablePreview({ chip, customer, onClose }: { chip: ComposerChip; customer: string; onClose: () => void }) {
  // The variable's own name is the lookup key, not its path — the codes
  // endpoint takes variable_id, which is what the vartree calls value1.
  const variableName = chip.path.includes('/') ? chip.path.slice(chip.path.lastIndexOf('/') + 1) : chip.path

  const codesQuery = useQuery({
    queryKey: ['vartree-codes', customer, chip.job, variableName],
    queryFn: async () => {
      const url = `${API_BASE_URL}/job/${customer}/${chip.job}/vartree/codes?variable_id=${encodeURIComponent(variableName)}`
      const res = await axios.get<GenNode[]>(url)
      return res.data?.[0]?.children || []
    },
  })

  const body = () => {
    if (codesQuery.isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress size={24} />
        </Box>
      )
    }
    if (codesQuery.isError) {
      return (
        <Typography sx={{ p: 2, fontSize: 13, color: 'error.main' }}>
          Could not load this variable&apos;s codes.
        </Typography>
      )
    }
    const codes = codesQuery.data || []
    if (codes.length === 0) {
      // A variable with no codes is a real thing (numerics, open text), not an
      // error — say which it is rather than showing an empty box.
      return (
        <Typography sx={{ p: 2, fontSize: 13, color: 'text.secondary' }}>
          This variable has no code list.
        </Typography>
      )
    }
    return (
      <List dense data-testid="variable-code-list">
        {codes.map((code, i) => (
          <ListItem key={`${code.value1}-${i}`} divider>
            <ListItemText
              primary={getNodeTitle(code)}
              primaryTypographyProps={{ fontSize: 13 }}
            />
          </ListItem>
        ))}
      </List>
    )
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" noWrap>{chip.label}</Typography>
            {/* The same line the chip's hover text carries: what it is, and
                where it came from. */}
            <Typography variant="caption" color="text.secondary">{chip.tooltip}</Typography>
          </Box>
          <IconButton onClick={onClose} size="small" aria-label="Close preview">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>{body()}</DialogContent>
    </Dialog>
  )
}

/** The dashboard half (Adam, 11 Sep): the whole dashboard, read-only, drawn
 *  by the same inline widget a create_dashboard tool call puts in the chat. */
function DashboardPreview({ chip, customer, onClose }: { chip: ComposerChip; customer: string; onClose: () => void }) {
  const { dashboard, dashboardIsLoading, dashboardIsError } = useDashboards({ customer, dashboardId: chip.path })
  const body = () => {
    if (dashboardIsLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress size={24} />
        </Box>
      )
    }
    if (dashboardIsError || !dashboard?.config) {
      return (
        <Typography sx={{ p: 2, fontSize: 13, color: 'error.main' }}>
          Could not load this dashboard.
        </Typography>
      )
    }
    return (
      <Box sx={{ p: 2 }}>
        <InlineDashboard
          dashboardId={dashboard.id}
          customer={customer}
          config={dashboard.config}
          viewUrl={`/dashboards/${encodeURIComponent(customer)}/${dashboard.id}/view`}
        />
      </Box>
    )
  }
  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" noWrap>{chip.label}</Typography>
            <Typography variant="caption" color="text.secondary">{chip.tooltip}</Typography>
          </Box>
          <IconButton onClick={onClose} size="small" aria-label="Close preview">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>{body()}</DialogContent>
    </Dialog>
  )
}

export default function AgentAttachmentPreview({ chip, customer, onClose }: AgentAttachmentPreviewProps) {
  if (!chip) return null

  if (chip.kind === 'dashboard') {
    return <DashboardPreview chip={chip} customer={customer} onClose={onClose} />
  }

  if (chip.kind === 'table') {
    return (
      <TableCitationModal
        open
        onClose={onClose}
        customer={customer}
        job={chip.job}
        tableName={chip.path}
      />
    )
  }

  return <VariablePreview chip={chip} customer={customer} onClose={onClose} />
}
